import { stringify } from 'qs';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { ContentStackRawAsset, ContentStackRawAssetWithColor, CsAssetSize } from './content-item';
import { ImageSource } from './types';

/**
 * IMAGE FORMAT STANDARDS FOR CONTENTSTACK RESPONSIVE IMAGES
 * =========================================================
 *
 * FORMAT ORDERING (browser selection priority):
 * 1. AVIF - Best compression (~50% smaller than WebP)
 * 2. WebP - Good compression (~25-30% smaller than JPEG)
 * 3. JPEG - Universal fallback (all browsers)
 *
 * CONTENTSTACK FORMAT PARAMETERS:
 * - format=avif (modern browsers: Chrome 85+, Firefox 93+, Safari 16.1+)
 * - format=webp (wide support: Chrome 23+, Firefox 65+, Safari 14+)
 * - format=jpg (universal support - NOTE: Contentstack uses 'jpg' not 'jpeg')
 *
 * QUALITY PARAMETER:
 * - Range: 1-100 (percentage)
 * - Only works with lossy formats: JPEG, Progressive JPEG, AVIF, WebP (Lossy)
 */

// The biggest we'll allow an image to go, this 2160 size is used by other Gatsby Image implementations
const MAX_WIDTH = 2160;
// Default quality level for all images
const QUALITY = 75;

// Standard responsive breakpoints (commonly used widths)
const RESPONSIVE_BREAKPOINTS = [180, 360, 540, 720, 1080, 1440, 1920] as const;

// Contentstack format configuration with proper type mapping
/*
* This is now the source of truth for what gets generated.
*/
const CONTENTSTACK_FORMATS = {
    avif: {
        format: 'avif' as const,
        type: 'image/avif' as const,
        supportsQuality: true,
    },
    webp: {
        format: 'webp' as const,
        type: 'image/webp' as const,
        supportsQuality: true,
    },
    jpg: {
        format: 'jpg' as const, // Contentstack uses 'jpg', not 'jpeg'
        type: 'image/jpeg' as const,
        supportsQuality: true,
    },
} as const;

export type ImageFormat = keyof typeof CONTENTSTACK_FORMATS;

const IMAGE_FORMATS = Object.keys(CONTENTSTACK_FORMATS) as ImageFormat[];

/**
 * Generates image source configuration for Contentstack API
 * @param asset - The raw asset from Contentstack
 * @param width - Target width for the image
 * @param params - Additional query parameters (format, quality, etc.)
 * @returns ImageSource configuration object
 */
const generateImageSource = (
    asset: ContentStackRawAsset,
    width: number,
    params: Record<string, string|number> = {},
): ImageSource => {
    // Get the base filename without extension
    let contentStackSrc = asset.url;

    if (asset.content_type !== 'image/svg+xml') {
        contentStackSrc += `?${stringify({
            ...params,
            width,
            quality: QUALITY,
        })}`;
    }

    const asUrl = new URL(contentStackSrc);
    const finalSrcWithoutHost = `${asUrl.pathname}${asUrl.search}`;

    if (process.env.NETLIFY) {
        return {
            finalSrc: `/dcs-images${finalSrcWithoutHost}`,
            contentStackSrc,
            width,
        };
    }

    return {
        finalSrc: contentStackSrc,
        contentStackSrc,
        width,
    };
};

/**
 * Generates responsive image sources for multiple formats using standard breakpoints
 * @param asset - The raw asset from Contentstack
 * @param targetWidth - The target display width
 * @param formats - Array of image formats (avif, webp, jpg)
 * @returns Object mapping format to array of ImageSource objects for different sizes
 */
export const generateResponsiveImageSources = (
    asset: ContentStackRawAsset,
    targetWidth: number,
    formats: ImageFormat[],
): Record<ImageFormat, ImageSource[]> => {
    const result: Record<ImageFormat, ImageSource[]> = {} as Record<ImageFormat, ImageSource[]>;
    formats.forEach((format) => {
        const formatConfig = CONTENTSTACK_FORMATS[format];

        // Filter breakpoints that don't exceed target width or MAX_WIDTH
        const maxAllowedWidth = Math.min(targetWidth, MAX_WIDTH);
        let relevantBreakpoints = RESPONSIVE_BREAKPOINTS.filter(
            breakpoint => breakpoint <= maxAllowedWidth,
        );

        // If no breakpoints match (image too small), use the smallest breakpoint
        // This ensures small images still generate at least one source
        if (relevantBreakpoints.length === 0) {
            relevantBreakpoints = [RESPONSIVE_BREAKPOINTS[0]];
        }

        result[format] = relevantBreakpoints.map((width) => {
            const params: Record<string, string|number> = {
                width,
                format: formatConfig.format,
            };
            if (formatConfig.supportsQuality) {
                params.quality = QUALITY;
            }
            return generateImageSource(asset, width, params);
        });
    });
    return result;
};

type WidthAndHeight = { width: number, height: number };
type WidthHeightSize = { width: number, height: number, size: CsAssetSize };

const CONTAINER_MAX = 1440;
const MOBILE_BREAKPOINT = 767;

const clamp = (p: number): number => Math.round(CONTAINER_MAX * (p / 100));

/**
 * Derives the target width in pixels from a CsAssetSize enum
 * This allows width to be automatically calculated when only size is provided
 */
export const getWidthFromSize = (size: CsAssetSize): number => {
    switch (size) {
        case CsAssetSize.QTR_100VW:
        case CsAssetSize.QTR_MAX_WIDTH:
            return 360; // 25% of 1440px container
        case CsAssetSize.THIRD_100VW:
        case CsAssetSize.THIRD_MAX_WIDTH:
            return 480; // 33.33% of 1440px container
        case CsAssetSize.HALF_100VW:
        case CsAssetSize.HALF_MAX_WIDTH:
            return 720; // 50% of 1440px container
        case CsAssetSize.FULL_100VW:
        case CsAssetSize.FULL_MAX_WIDTH:
        default:
            return 1440; // Full container width
    }
};

export const getSizesProp = (
    size: CsAssetSize,
): string => {
    switch (size) {
        case CsAssetSize.FULL_100VW:
            return '100vw';
        case CsAssetSize.HALF_100VW: {
            const base = '50vw';
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, ${base}`;
        }
        case CsAssetSize.THIRD_100VW: {
            const base = '33.33vw';
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, ${base}`;
        }
        case CsAssetSize.QTR_100VW: {
            const base = '25vw';
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, ${base}`;
        }
        case CsAssetSize.FULL_MAX_WIDTH: {
            return `(max-width: ${CONTAINER_MAX}px) 100vw, ${CONTAINER_MAX}px`;
        }
        case CsAssetSize.HALF_MAX_WIDTH: {
            const base = 50;
            const pxClamp = clamp(base);
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, (max-width: ${CONTAINER_MAX}px) ${base}vw, ${pxClamp}px`;
        }
        case CsAssetSize.THIRD_MAX_WIDTH: {
            const base = 33.33;
            const pxClamp = clamp(base);
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, (max-width: ${CONTAINER_MAX}px) ${base}vw, ${pxClamp}px`;
        }
        case CsAssetSize.QTR_MAX_WIDTH: {
            const base = 25;
            const pxClamp = clamp(base);
            return `(max-width: ${MOBILE_BREAKPOINT}px) 100vw, (max-width: ${CONTAINER_MAX}px) ${base}vw, ${pxClamp}px`;
        }
        default:
            return '100vw';
    }
};

const getSizeAfterTransformations = (
    original: WidthAndHeight,
    requested: Partial<WidthAndHeight>,
): WidthAndHeight => {
    let { width, height } = original;

    if (requested?.width) {
        const scales = [];

        if (requested?.width) {
            scales.push(requested.width / width);
        }

        if (requested?.height) {
            scales.push(requested.height / height);
        }

        const scale = Math.min(...scales);

        width = Math.round(scale * width);
        height = Math.round(scale * height);

        return { width, height };
    }

    return { width, height };
};

/**
 * Generates properly ordered sources for <picture> element
 * Format order: AVIF first (best compression), then WebP, then JPEG fallback
 */
const generateResolvedImage = (
    imageAsset: ContentStackRawAssetWithColor,
    formatSources: Record<ImageFormat, ImageSource[]>,
    size: WidthHeightSize,
): ResolvedImageData => {
    // Defensive: fallback to last jpg, or empty string
    const fallbackArr = formatSources.jpg || [];
    const fallbackJpg = fallbackArr.length > 0 ? fallbackArr[fallbackArr.length - 1] : { finalSrc: '', width: 0 };
    const sizeProp = getSizesProp(size.size);
    // Build sources array dynamically, excluding fallback (jpg)
    const sources = IMAGE_FORMATS.filter(format => format !== 'jpg')
        .map(format => ({
            type: CONTENTSTACK_FORMATS[format].type,
            srcSet: (formatSources[format] || []).map(x => `${x.finalSrc} ${x.width}w`).join(',\n'),
            sizes: sizeProp,
        }));
    return {
        childImageSharp: imageAsset.content_type === 'image/svg+xml' ? null : {
            images: {
                sources,
                fallback: {
                    src: fallbackJpg.finalSrc,
                    srcSet: fallbackArr.map(x => `${x.finalSrc} ${x.width}w`).join(',\n'),
                    sizes: sizeProp,
                },
            },
            layout: 'constrained',
            backgroundColor: imageAsset.color || '',
            width: size.width,
            height: size.height,
        },
        publicURL: fallbackJpg.finalSrc,
    };
};

export const processImagePublicUrl = async (
    imageAsset: ContentStackRawAsset,
    resizeParams: Partial<WidthHeightSize>,
): Promise<ResolvedImageData|null> => {
    const {
        dimension,
    } = imageAsset;

    if (!dimension) return null;

    const finalSize = getSizeAfterTransformations(
        dimension,
        resizeParams,
    );

    // Handle SVG files - no format conversion needed
    if (imageAsset.content_type === 'image/svg+xml') {
        const svgSource = generateImageSource(
            imageAsset,
            finalSize.width,
            {},
        );

        const svgSources = IMAGE_FORMATS.reduce((acc, format) => {
            acc[format] = [svgSource];
            return acc;
        }, {} as Record<ImageFormat, ImageSource[]>);
        return generateResolvedImage(imageAsset, svgSources, {
            ...finalSize,
            size: CsAssetSize.FULL_100VW,
        });
    }

    // Generate all sources in one call
    const sources = generateResponsiveImageSources(imageAsset, finalSize.width, IMAGE_FORMATS);
    return generateResolvedImage(imageAsset, sources, {
        ...finalSize,
        size: CsAssetSize.FULL_100VW,
    });
};

type ResolvedImageData = {
    childImageSharp: IGatsbyImageData|null
    publicURL: string
};

/**
 * Main function for processing responsive images with multiple formats and densities
 * Generates AVIF, WebP, and JPEG sources at different density levels
 */
const processImageResolution = async (
    imageAsset: ContentStackRawAssetWithColor,
    resizeParams: Partial<WidthHeightSize>,
): Promise<ResolvedImageData|null> => {
    const {
        dimension,
    } = imageAsset;

    if (!dimension) return null;

    // Auto-infer width from size if not explicitly provided
    const effectiveParams = { ...resizeParams };
    if (!effectiveParams.width && effectiveParams.size) {
        effectiveParams.width = getWidthFromSize(effectiveParams.size);
    }

    const finalSize = getSizeAfterTransformations(
        dimension,
        effectiveParams,
    );

    // Handle SVG files - no format conversion needed
    if (imageAsset.content_type === 'image/svg+xml') {
        const svgSource = generateImageSource(
            imageAsset,
            finalSize.width,
            {},
        );
        // DRY: build svgSources with all IMAGE_FORMATS keys
        const svgSources = IMAGE_FORMATS.reduce((acc, format) => {
            acc[format] = [svgSource];
            return acc;
        }, {} as Record<ImageFormat, ImageSource[]>);
        return generateResolvedImage(imageAsset, svgSources, {
            ...finalSize,
            size: effectiveParams.size || CsAssetSize.FULL_100VW,
        });
    }

    // Generate all sources in one call
    const sources = generateResponsiveImageSources(imageAsset, finalSize.width, IMAGE_FORMATS);
    return generateResolvedImage(imageAsset, sources, {
        ...finalSize,
        size: effectiveParams.size || CsAssetSize.FULL_100VW,
    });
};

export default processImageResolution;
